import React, { FunctionComponent, useState, useEffect } from 'react'
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useIntercom } from 'react-use-intercom'
import { logOut } from 'Redux/actions/auth'
import {
  getIsArtist,
  getUserSelector,
  getUserRole,
  getIsNewFeed,
} from 'Redux/selectors/auth'
import { removeCookiesHandler } from 'Constants/functions'
import { getTotalAwaitingTracksSelector } from 'Redux/selectors/reviews'
import { getTotalAwaitingTracksSelector as getTotalFeedReviews } from 'Redux/selectors/feed'
import { APP } from 'Configs/App'
import { Roles } from 'Constants/enums'
import {
  campaignsList,
  playlists,
  reviews,
  tracksFeed,
  balance,
  settings,
  rank,
  influencerBoard,
} from 'Router/routes'
import { logOutEvent } from 'Helpers/tagManager'

import {
  IconCampaigns,
  IconSettings,
  IconHelpCenter,
  IconBalance,
  IconReviews,
  IconFeed,
  IconRank,
  IconPlaylists,
  TracksIcon,
} from 'Stories/Icon/Icon.stories'
import SidebarLink from '../SidebarLink'
import SideBarTop from '../SideBarTop'
import SideBarBottom from '../SideBarBottom'
import AdminBlock from '../AdminBlock'

import { SidebarContentProps } from './SidebarContentProps'

import {
  SidebarContentWrapper,
  SidebarList,
  SidebarLinkItem,
  SidebarIconBlock,
  SidebarLinkText,
  Top,
} from './styles'

const SidebarContent: FunctionComponent<SidebarContentProps> = ({
  onClick,
  collapse,
}) => {
  const { t } = useTranslation()
  const { hardShutdown, show } = useIntercom()

  const dispatch = useDispatch()

  const isArtist: boolean = useSelector(getIsArtist)
  const user = useSelector(getUserSelector)
  const userRole = useSelector(getUserRole)
  const isNewFeed = useSelector(getIsNewFeed)
  const awaitingReviewTotal: number = useSelector(
    getTotalAwaitingTracksSelector
  )
  const feedReviewsTotal = useSelector(getTotalFeedReviews)
  const currentBalance: number = useSelector(
    (state: RootStateOrAny) => state.auth.user.balance
  )
  const [isDefaultImage, setIsDefaultImage] = useState(!user.user_photo_url)
  const [isSubAnalyticsLinkOpen, setIsSubAnalyticsLinkOpen] = useState(false)
  const toggleSubAnalyticsLinkOpen = () => {
    setIsSubAnalyticsLinkOpen(!isSubAnalyticsLinkOpen)
  }

  const onClickHandle = () => {
    onClick && onClick()
    setIsSubAnalyticsLinkOpen(false)
  }

  const handleLogOut = () => {
    hardShutdown()
    user && logOutEvent(user)
    dispatch(logOut())
    removeCookiesHandler()
    localStorage.removeItem('user')
    localStorage.removeItem('alreadyExistedEmail')
  }

  const onImageLoadError = () => {
    setIsDefaultImage(true)
  }

  useEffect(() => {
    user.user_photo_url && setIsDefaultImage(false)
  }, [user.user_photo_url])

  return (
    <SidebarContentWrapper>
      <SideBarTop
        collapse={collapse}
        isDefaultImage={isDefaultImage}
        onImageLoadError={onImageLoadError}
      />

      {isArtist && (
        <SidebarList>
          <Top>
            <SidebarLink
              icon={<IconCampaigns />}
              route={campaignsList}
              title={t('sidebar.campaigns')}
              onClick={onClickHandle}
            />
            <SidebarLink
              icon={<IconSettings />}
              route={settings}
              title={t('sidebar.settings')}
              onClick={onClickHandle}
            />

            <a
              href="http://help.soundcamps.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SidebarLinkItem>
                <SidebarIconBlock>
                  <IconHelpCenter />
                </SidebarIconBlock>
                {!collapse ? (
                  <SidebarLinkText>{t('sidebar.helpCenter')}</SidebarLinkText>
                ) : (
                  <SidebarLinkText />
                )}
              </SidebarLinkItem>
            </a>
            {APP.admin_email.includes(user.email) && (
              <AdminBlock
                toggleSubAnalyticsLinkOpen={toggleSubAnalyticsLinkOpen}
                collapse={collapse}
                isSubAnalyticsLinkOpen={isSubAnalyticsLinkOpen}
                onClickHandle={onClickHandle}
              />
            )}
          </Top>
          <SideBarBottom collapse={collapse} handleLogOut={handleLogOut} />
        </SidebarList>
      )}

      {!isArtist && userRole !== Roles.influencer && (
        <SidebarList>
          <Top>
            <SidebarLink
              icon={<IconReviews />}
              reviewsLength={awaitingReviewTotal}
              route={reviews}
              title={t('sidebar.reviews')}
              onClick={onClickHandle}
            />
            <SidebarLink
              icon={<IconFeed />}
              reviewsLength={feedReviewsTotal}
              route={tracksFeed}
              title={t('sidebar.tracksFeed')}
              isNew={isNewFeed}
              onClick={onClickHandle}
            />
            <SidebarLink
              balance={currentBalance?.toFixed(2)}
              icon={<IconBalance />}
              route={balance}
              title={t('sidebar.balance')}
              onClick={onClickHandle}
            />
            <SidebarLink
              icon={<IconRank />}
              route={rank}
              title={t('sidebar.rank')}
              onClick={onClickHandle}
            />
            <SidebarLink
              icon={<IconPlaylists />}
              route={playlists}
              title={t('sidebar.playlists')}
              onClick={onClickHandle}
            />
            <SidebarLink
              icon={<IconSettings />}
              route={settings}
              title={t('sidebar.settings')}
              onClick={onClickHandle}
            />
            {APP.admin_email.includes(user.email) && (
              <AdminBlock
                toggleSubAnalyticsLinkOpen={toggleSubAnalyticsLinkOpen}
                collapse={collapse}
                isSubAnalyticsLinkOpen={isSubAnalyticsLinkOpen}
                onClickHandle={onClickHandle}
              />
            )}
            <a
              href="http://help.soundcamps.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SidebarLinkItem>
                <SidebarIconBlock>
                  <IconHelpCenter />
                </SidebarIconBlock>
                {!collapse ? (
                  <SidebarLinkText>{t('sidebar.helpCenter')}</SidebarLinkText>
                ) : (
                  <SidebarLinkText />
                )}
              </SidebarLinkItem>
            </a>
          </Top>
          <SideBarBottom collapse={collapse} handleLogOut={handleLogOut} />
        </SidebarList>
      )}

      {userRole === Roles.influencer && (
        <SidebarList>
          <Top>
            <SidebarLink
              icon={<TracksIcon />}
              route={influencerBoard}
              title={t('sidebar.tracks')}
              onClick={onClickHandle}
            />
            <a
              href="http://help.soundcamps.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SidebarLinkItem>
                <SidebarIconBlock>
                  <IconHelpCenter />
                </SidebarIconBlock>
                {!collapse ? (
                  <SidebarLinkText>{t('sidebar.helpCenter')}</SidebarLinkText>
                ) : (
                  <SidebarLinkText />
                )}
              </SidebarLinkItem>
            </a>
          </Top>
          <SideBarBottom collapse={collapse} handleLogOut={handleLogOut} />
        </SidebarList>
      )}
    </SidebarContentWrapper>
  )
}

export default SidebarContent
